import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PrimaryOutlinedButton } from "@shared/CustomButton";

import type { EnrollmentPath } from "../EnrollmentPage";

const useStyles = makeStyles()((theme: Theme) => {
    const isDark = theme.palette.mode === "dark";

    return {
        container: {
            maxWidth: 900,
            width: "100%",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(6),
        },
        cardGrid: {
            display: "grid",
            gap: theme.spacing(4),
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            [theme.breakpoints.down("md")]: {
                gridTemplateColumns: "1fr",
            },
        },
        optionCard: {
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(3.5),
            width: "100%",
            padding: theme.spacing(5),
            borderRadius: theme.spacing(2),
            border: `1px solid ${theme.palette.emteria.border.primary}`,
            backgroundColor: theme.palette.emteria.text.inverse,
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
                borderColor: theme.palette.emteria.action,
                boxShadow: theme.shadows[2],
            },
            "&:focus-visible": {
                outline: `2px solid ${theme.palette.emteria.action}`,
                outlineOffset: 2,
            },
        },
        iconWrapper: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: 12,
            backgroundColor: isDark ? alpha(theme.palette.emteria.text.primary, 0.15) : alpha(theme.palette.emteria.action, 0.08),
            flexShrink: 0,
        },
        icon: {
            fontSize: 20,
            color: isDark ? theme.palette.emteria.text.primary : theme.palette.emteria.action,
        },
        optionTitle: {
            fontWeight: 600,
            color: theme.palette.emteria.text.primary,
            textAlign: "left",
        },
        optionDescription: {
            color: theme.palette.emteria.text.secondary,
            lineHeight: 1.5,
        },
        footer: {
            display: "flex",
            justifyContent: "center",
            paddingTop: theme.spacing(2),
        },
        helpIcon: {
            fontSize: 15,
        },
    };
});

type EnrollmentOption = {
    path: EnrollmentPath;
    iconClassName: string;
    title: string;
    description: string;
};

interface EnrollmentTypeSelectionStepProps {
    onContinue: () => void;
    onPathSelect?: (path: EnrollmentPath) => void;
    selectedPath?: EnrollmentPath;
}

type EnrollmentOptionCardProps = {
    option: EnrollmentOption;
    onSelect: (path: EnrollmentPath) => void;
    classes: ReturnType<typeof useStyles>["classes"];
};

const EnrollmentOptionCard = ({ option, onSelect, classes }: EnrollmentOptionCardProps) => {
    const handleSelect = () => {
        onSelect(option.path);
    };

    return (
        <Box
            component="button"
            type="button"
            className={classes.optionCard}
            onClick={handleSelect}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSelect();
                }
            }}
        >
            <Box className={classes.iconWrapper}>
                <i className={clsx(option.iconClassName, classes.icon)} aria-hidden />
            </Box>
            <Typography variant="h6" className={classes.optionTitle}>
                {option.title}
            </Typography>
            <Typography variant="body1" className={classes.optionDescription}>
                {option.description}
            </Typography>
        </Box>
    );
};

export const EnrollmentTypeSelectionStep: React.FC<EnrollmentTypeSelectionStepProps> = ({
    onContinue,
    onPathSelect,
    selectedPath,
}) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals"]);

    const enrollmentOptions: EnrollmentOption[] = [
        {
            path: "install_android_os",
            iconClassName: "fa-solid fa-download",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.installAndroidOs.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.installAndroidOs.description"),
        },
        {
            path: "install_mdm",
            iconClassName: "fa-brands fa-android",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.installMdm.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.installMdm.description"),
        },
        {
            path: "enroll_existing",
            iconClassName: "fa-solid fa-qrcode",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.enrollExisting.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.enrollExisting.description"),
        },
        {
            path: "build_custom",
            iconClassName: "fa-solid fa-screwdriver-wrench",
            title: t("modals:enrollmentDialog.enrollFirstDevice.cards.buildCustom.title"),
            description: t("modals:enrollmentDialog.enrollFirstDevice.cards.buildCustom.description"),
        },
    ];

    const handleGetHelp = () => {
        window.open("https://emteria.com/lp/demo", "_blank");
    };

    const handlePathSelect = (path: EnrollmentPath) => {
        onPathSelect?.(path);
        onContinue();
    };

    React.useEffect(() => {
        if (selectedPath) {
            onPathSelect?.(selectedPath);
        }
    }, [selectedPath, onPathSelect]);

    return (
        <Box className={classes.container}>
            <Box className={classes.cardGrid}>
                {enrollmentOptions.map((option) => (
                    <EnrollmentOptionCard key={option.path} option={option} onSelect={handlePathSelect} classes={classes} />
                ))}
            </Box>
            <Box className={classes.footer}>
                <PrimaryOutlinedButton
                    onClick={handleGetHelp}
                    startIcon={
                        <Box component="span">
                            <i className={clsx("fa-solid fa-headset", classes.helpIcon)} />
                        </Box>
                    }
                >
                    {t("modals:enrollmentDialog.enrollFirstDevice.buttons.getHelp")}
                </PrimaryOutlinedButton>
            </Box>
        </Box>
    );
};
